import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ValidationErrors } from "pojo-validator";
import { ValidateCallback } from "pojo-validator-react";
import { ValidatedInput } from "pojo-validator-reactstrap";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonGroup, Card, CardBody, Col, Collapse, FormGroup, FormText, InputGroup, InputGroupAddon, Input, Label, Row } from "reactstrap";
import { useToggleState } from "use-toggle-state";
import { QuestionAnswer } from "../../../../api/main/models/QuestionAnswer";
import { HtmlDisplay, HtmlEditor } from "../../../../shared/htmlEditor";
import { CardHeaderCollapse } from "../../../shared/cardHeaderCollapse/CardHeaderCollapse";
import { FormButtons } from "../../../shared/FormButtons";

export interface QuestionAnswerLocationResponseImageProps {
    model: QuestionAnswer | undefined,
    change: (changes: Partial<QuestionAnswer>) => void,
    remove: () => void,
    moveUp: () => void,
    canMoveUp: boolean,
    moveDown: () => void,
    canMoveDown: boolean,

    isOpenInitially?: boolean,

    validate: ValidateCallback,
    validationErrors: ValidationErrors,
}

/**
 * Response window for a LocationResponseImage question.
 * @param props
 */
export const QuestionAnswerLocationResponseImage = (props: QuestionAnswerLocationResponseImageProps) => {
    const {
        model,
        change,
        remove,
        moveUp,
        canMoveUp,
        moveDown,
        canMoveDown,

        isOpenInitially = false,

        validate,
        validationErrors,
    } = props;

    const { t } = useTranslation();

    // Self management of if we are expanded or not.
    const [isOpen, toggleOpen] = useToggleState(isOpenInitially);

    const locationString = useMemo(() => {
        if (!model) {
            return '';
        }

        return `${Math.round(model.windowTop)},${Math.round(model.windowLeft)} to ${Math.round(model.windowBottom)},${Math.round(model.windowRight)}`;
    }, [model]);

    return (
        <Card>
            <CardHeaderCollapse isOpen={isOpen} toggle={toggleOpen}>
                <Row>
                    <Col xs="auto">
                        {locationString}
                    </Col>
                    <Col>
                        <div style={{ maxHeight: '2rem', overflowY: 'hidden', }}>
                            <HtmlDisplay html={model?.answerText ?? ''} />
                        </div>
                    </Col>
                </Row>
            </CardHeaderCollapse>

            <Collapse isOpen={isOpen}>
                <CardBody>
                    <FormButtons noPadding>
                        <ButtonGroup>
                            <Button color="primary" outline onClick={() => moveUp()} disabled={!canMoveUp}>
                                <FontAwesomeIcon icon="caret-up" />
                                <> </>
                                {t('questionAnswerLocationResponseImage.up', 'Up')}
                            </Button>
                            <Button color="primary" outline onClick={() => moveDown()} disabled={!canMoveDown}>
                                <FontAwesomeIcon icon="caret-down" />
                                <> </>
                                {t('questionAnswerLocationResponseImage.down', 'Down')}
                            </Button>
                            <Button color="danger" outline onClick={() => remove()}>
                                <FontAwesomeIcon icon="trash-alt" />
                                <> </>
                                {t('questionAnswerLocationResponseImage.delete', 'Delete window')}
                            </Button>
                        </ButtonGroup>
                    </FormButtons>

                    <Row>
                        <Col>
                            <FormGroup>
                                <Label htmlFor="windowTop">{t('questionAnswerLocationResponseImage.windowTop.label', 'Top')}</Label>

                                <Input type="number" value={model?.windowTop ?? ''} onChange={e => change({ windowTop: e.currentTarget.valueAsNumber })} />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup>
                                <Label htmlFor="windowLeft">{t('questionAnswerLocationResponseImage.windowLeft.label', 'Left')}</Label>

                                <Input type="number" value={model?.windowLeft ?? ''} onChange={e => change({ windowLeft: e.currentTarget.valueAsNumber })} />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <FormGroup>
                                <Label htmlFor="windowBottom">{t('questionAnswerLocationResponseImage.windowBottom.label', 'Bottom')}</Label>

                                <Input type="number" value={model?.windowBottom ?? ''} onChange={e => change({ windowBottom: e.currentTarget.valueAsNumber })} />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup>
                                <Label htmlFor="windowRight">{t('questionAnswerLocationResponseImage.windowRight.label', 'Right')}</Label>

                                <Input type="number" value={model?.windowRight ?? ''} onChange={e => change({ windowRight: e.currentTarget.valueAsNumber })} />
                            </FormGroup>
                        </Col>
                    </Row>
                    

                    <FormGroup>
                        <Label htmlFor="answerText">{t('questionAnswerLocationResponseImage.answerText', 'Window description')}</Label>
                        <HtmlEditor value={model?.answerText ?? ''} onChange={html => change({ answerText: html })} />
                    </FormGroup>

                    <Row>
                        {/*<Col>*/}
                        {/*    <FormGroup>*/}
                        {/*        <Label htmlFor="isCorrect">{t('questionAnswerLocationResponseImage.isCorrect', 'Is this a correct answer?')}</Label>*/}

                        {/*        <TwoValueSwitch*/}
                        {/*            leftLabel={t('questionAnswerLocationResponseImage.isCorrect.false', 'Incorrect')}*/}
                        {/*            rightLabel={t('questionAnswerLocationResponseImage.isCorrect.true', 'Correct')}*/}
                        {/*            checked={model?.isCorrect ?? false} onChange={checked => {*/}
                        {/*                // Toggle the flag, and if we're using default scoring at the moment, also default the score.*/}
                        {/*                if (checked && model?.score === 0) {*/}
                        {/*                    change({ isCorrect: checked, score: 10 });*/}
                        {/*                } else if (!checked && model?.score === 10) {*/}
                        {/*                    change({ isCorrect: checked, score: 0 });*/}
                        {/*                } else {*/}
                        {/*                    change({ isCorrect: checked });*/}
                        {/*                }*/}
                        {/*            }}*/}
                        {/*            />*/}
                        {/*    </FormGroup>*/}
                        {/*</Col>*/}
                        <Col>
                            <FormGroup>
                                <Label htmlFor="score">{t('questionAnswerLocationResponseImage.score.label', 'Score')}</Label>

                                <InputGroup>
                                    <ValidatedInput name="score" type="number" min={-100} max={100} value={model?.score ?? ''} onChange={e => change({ score: e.currentTarget.valueAsNumber })} onBlur={() => validate('score')} validationErrors={validationErrors['score']} />
                                    <InputGroupAddon addonType="append">
                                        {t('common.percentageSign', '%')}
                                    </InputGroupAddon>
                                </InputGroup>
                                <FormText>
                                    {t('questionAnswerMultipleChoiceVideo.score.help', 'Score will normally be between 0% (incorrect) and 100% (correct).')}
                                </FormText>
                            </FormGroup>
                        </Col>
                    </Row>
                </CardBody>
            </Collapse>
        </Card>
        );
};